import { Injectable } from '@angular/core';
import {
  BatchUpdateCustomerTpinRequest,
  BatchUpdateCustomerTpinResponse,
  CustomerTpinResponse,
  UpdateCustomerTpinRequest,
  ZraCustomerTpinService,
} from '@boma/supplier-portal/shared/services';
import { Store } from '@ngrx/store';
import { getSelectedOrganizationInfo } from '../shared-state';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { BehaviorSubject } from 'rxjs';

export interface ZraCustomerTpinError {
  message: string;
  status?: number;
  timestamp?: string;
}

export interface ZraCustomerTpinState {
  customerTpins: Map<string, CustomerTpinResponse>;
  loading: boolean;
  updateLoading: boolean;
  batchUpdateLoading: boolean;
  syncLoading: boolean;
  error: ZraCustomerTpinError | null;
}

@Injectable()
export class ZraCustomerTpinFacade {
  private readonly _entityId$: Observable<string>;
  private readonly _state$: BehaviorSubject<ZraCustomerTpinState> =
    new BehaviorSubject<ZraCustomerTpinState>({
      customerTpins: new Map(),
      loading: false,
      updateLoading: false,
      batchUpdateLoading: false,
      syncLoading: false,
      error: null,
    });

  getCustomerTpin$(
    customerId: string
  ): Observable<CustomerTpinResponse | undefined> {
    return this._state$.pipe(
      map((state) => state.customerTpins.get(customerId))
    );
  }

  customerTpins$: Observable<CustomerTpinResponse[]> = this._state$.pipe(
    map((state) => Array.from(state.customerTpins.values()))
  );

  loading$: Observable<boolean> = this._state$.pipe(
    map((state) => state.loading)
  );
  
  updating$: Observable<boolean> = this._state$.pipe(
    map((state) => state.updateLoading)
  );
  
  batchUpdating$: Observable<boolean> = this._state$.pipe(
    map((state) => state.batchUpdateLoading)
  );
  
  syncLoading$: Observable<boolean> = this._state$.pipe(
    map((state) => state.syncLoading)
  );
  
  error$: Observable<ZraCustomerTpinError | null> = this._state$.pipe(
    map((state) => state.error)
  );

  constructor(
    private store: Store,
    private zraCustomerTpinService: ZraCustomerTpinService
  ) {
    this._entityId$ = this.store
      .select(getSelectedOrganizationInfo)
      .pipe(map((info) => info.id));
  }

  getCustomerTpin(customerId: string) {
    this._updateState({ loading: true, error: null });

    return this._entityId$
      .pipe(
        switchMap((entityId) =>
          this.zraCustomerTpinService
            .getCustomerTpin(entityId, customerId)
            .pipe(
              catchError((error) => {
                this._handleError('Failed to get customer TPIN details', error);
                return of(null);
              })
            )
        ),
        finalize(() => this._updateState({ loading: false }))
      )
      .subscribe({
        next: (customerTpin) => {
          if (customerTpin) {
            this._updateCustomerTpins(customerTpin);
          }
        },
      });
  }

  updateCustomerTpin(request: UpdateCustomerTpinRequest): Observable<CustomerTpinResponse | null> {
    this._updateState({ updateLoading: true, error: null });

    return this._entityId$.pipe(
      switchMap((entityId) =>
        this.zraCustomerTpinService.updateCustomerTpin(entityId, request).pipe(
          tap((customerTpin) => {
            if (customerTpin) {
              this._updateCustomerTpins(customerTpin);
            }
          }),
          catchError((error) => {
            this._handleError('Failed to update customer TPIN', error);
            this._updateState({ updateLoading: false });
            return of(null);
          }),
          finalize(() => this._updateState({ updateLoading: false }))
        )
      )
    );
  }

  batchUpdateCustomerTpin(request: BatchUpdateCustomerTpinRequest): Observable<BatchUpdateCustomerTpinResponse | null> {
    this._updateState({ batchUpdateLoading: true, error: null });

    return this._entityId$.pipe(
      switchMap((entityId) =>
        this.zraCustomerTpinService.batchUpdateCustomerTpin(entityId, request).pipe(
          tap((response) => {
            if (response && response.successCount > 0) {
              // After successful batch update, we could refresh specific customer details
              // if needed, but we don't have direct data to update our state here
            }
          }),
          catchError((error) => {
            this._handleError('Failed to batch update customer TPINs', error);
            this._updateState({ batchUpdateLoading: false });
            return of(null);
          }),
          finalize(() => this._updateState({ batchUpdateLoading: false }))
        )
      )
    );
  }

  syncCustomerTpin(customerId: string): Observable<{ message: string } | null> {
    this._updateState({ syncLoading: true, error: null });

    return this._entityId$.pipe(
      switchMap((entityId) =>
        this.zraCustomerTpinService.syncCustomerTpin(entityId, customerId).pipe(
          tap((response) => {
            // After successful sync, refresh the customer TPIN details
            if (response) {
              this.getCustomerTpin(customerId);
            }
          }),
          catchError((error) => {
            this._handleError('Failed to sync customer TPIN with ZRA', error);
            this._updateState({ syncLoading: false });
            return of(null);
          }),
          finalize(() => this._updateState({ syncLoading: false }))
        )
      )
    );
  }

  _updateCustomerTpins(customerTpin: CustomerTpinResponse): void {
    const updatedMap = new Map(this._state$.value.customerTpins);
    updatedMap.set(customerTpin.entityCustomerId, customerTpin);
    this._updateState({
      customerTpins: updatedMap,
      loading: false,
      updateLoading: false
    });
  }

  _updateState(state: Partial<ZraCustomerTpinState>): void {
    this._state$.next({ ...this._state$.value, ...state });
  }

  private _handleError(context: string, error: any): void {
    console.error(`${context}:`, error);

    const errorObj: ZraCustomerTpinError = {
      message: error.message || context,
      status: error.status,
      timestamp: new Date().toISOString(),
    };

    this._updateState({ error: errorObj, loading: false });
  }
} 